// src/components/StatBox.jsx
import React from "react";
import { Box, Typography } from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";

const StatBox = ({
  theme,
  logo,
  title,
  value,
  Icon,
  BgIcon,
  onClick,
  valueFormatter,
  deltaValue,
  deltaFormatter,
  deltaLabel = "",
  deltaPositiveColor = "#4ade80",
  deltaNegativeColor = "#dc2626",
  deltaNeutralColor = "#4ade80",
  showAcrossGroup = false,
  acrossGroupValue = "",
}) => {
  const DisplayIcon = Icon;
  const BackgroundIcon = BgIcon || Icon;

  const hasDelta = typeof deltaValue === "number" && !Number.isNaN(deltaValue);
  const hasAcrossGroup =
    typeof acrossGroupValue !== "undefined" && acrossGroupValue !== null;
  const deltaIsPositive = hasDelta && deltaValue > 0;
  const deltaIsNegative = hasDelta && deltaValue < 0;

  const deltaColor = deltaIsPositive
    ? deltaPositiveColor
    : deltaIsNegative
    ? deltaNegativeColor
    : deltaNeutralColor;

  const formattedDelta = hasDelta
    ? typeof deltaFormatter === "function"
      ? deltaFormatter(deltaValue)
      : deltaValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : null;

  return (
    <Box
      role={onClick ? "button" : undefined}
      onClick={onClick}
      className="rounded-lg shadow-lg transition-all duration-200"
      sx={{
        ...theme.container,
        position: "relative", // Required for absolute background icon
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: 2,
        minHeight: 140,
        transition: "all 0.2s ease-in-out",
        cursor: onClick ? "pointer" : "default",
        ...(onClick && { "&:hover": theme.hover }),
      }}
    >
      {BackgroundIcon && (
        <BackgroundIcon
          sx={{
            ...theme.backgroundIcon,
            position: "absolute",
            right: 8,
            bottom: 8,
            opacity: 0.15,
            pointerEvents: "none",
            userSelect: "none",
            fontSize: 64,
          }}
        />
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {logo ? (
          <Box
          
            component="img"
            src={logo}
            alt=""
            sx={{
              ...theme.logo,
              maxWidth: "100%",
              height: "34px",
              objectFit: "contain",
            }}
          />
        ) : null}

        <Typography
          variant="subtitle1"
          sx={{
            ...theme.title,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "1.3rem",
            width: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {DisplayIcon ? (
            <DisplayIcon sx={{ ...theme.icon, fontSize: 22 }} />
          ) : null}
          {title}
        </Typography>

        {hasDelta ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
            }}
          >
            {deltaIsNegative ? (
              <ArrowDropDown sx={{ color: deltaColor, fontSize: 24, ml: -0.5 }} />
            ) : (
              <ArrowDropUp sx={{ color: deltaColor, fontSize: 24, ml: -0.5 }} />
            )}

            <Typography
              variant="subtitle2"
              sx={{ color: deltaColor, fontWeight: 600, mr: 0.5 }}
            >
              {formattedDelta}
            </Typography>

            {deltaLabel ? (
              <Typography
                variant="caption"
                sx={{
                  color: deltaColor,
                  fontWeight: 500,
                  opacity: 0.95,
                }}
              >
                {deltaLabel}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {showAcrossGroup ? (
          <Typography
            variant="subtitle2"
            sx={{
              color: deltaColor,
              fontWeight: 500,
              opacity: 0.95,
            }}
          >
            Across Group: {acrossGroupValue}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            ...theme.value,
            fontSize: "3rem",
            lineHeight: 0.7,
            margin: 0,
            whiteSpace: "nowrap",
          }}
        >
          {typeof valueFormatter === "function" ? valueFormatter(value) : value}
        </Typography>
      </Box>
    </Box>
  );
};

export default StatBox;
